import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Notification, notificationApi } from '../services/notificationApi';
import { useAuth } from './AuthProvider';
import { DashboardBootstrapResponse } from '../services/dashboardApi';
import { useSocket } from '../hooks/useSocket';

interface NotificationContextType {
  // Toast notifications
  toastNotifications: ToastNotification[];
  addNotification: (message: string, type?: ToastNotification['type']) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  
  // Bell notifications  
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (id: number) => Promise<void>;
  markAsUnread: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: number) => Promise<void>;
  loading: boolean;
  subscribeToPush?: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

interface ToastNotification {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  timestamp: Date;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  // Try to get auth state, but don't fail if not in AuthProvider
  let isAuthenticated = false;
  try {
    const auth = useAuth();
    isAuthenticated = !!auth?.user && !!auth?.session;
  } catch (_error) {
    // Not within AuthProvider, assume not authenticated
    isAuthenticated = false;
  }
  const queryClient = useQueryClient();
  const lastFetchRef = useRef<number>(0);
  const hydratedFromDashboardRef = useRef<boolean>(false);
  
  // Toast notifications (existing functionality)
  const [toastNotifications, setToastNotifications] = useState<ToastNotification[]>([]);
  
  // Bell notifications (new functionality)
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Toast notification functions
  const removeNotification = useCallback((id: string) => {
    setToastNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const addNotification = useCallback((message: string, type: ToastNotification['type'] = 'info') => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const notification: ToastNotification = {
      id,
      message,
      type,
      timestamp: new Date()
    };
    
    setToastNotifications(prev => [...prev, notification]);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      removeNotification(id);
    }, 5000);
  }, [removeNotification]);

  const clearNotifications = useCallback(() => {
    setToastNotifications([]);
  }, []);

  // Bell notification functions
  const refreshNotifications = useCallback(async (force: boolean = false) => {
    // Only fetch notifications if user is authenticated
    if (!isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    const now = Date.now();
    const FETCH_DEBOUNCE_MS = 60 * 1000;
    if (!force && now - lastFetchRef.current < FETCH_DEBOUNCE_MS) {
      return;
    }
    
    try {
      setLoading(true);
      lastFetchRef.current = now;
      const [notificationsResponse, unreadCountResponse] = await Promise.all([
        notificationApi.getNotifications(1, 10),
        notificationApi.getUnreadCount()
      ]);
      setNotifications(notificationsResponse.notifications);
      setUnreadCount(unreadCountResponse);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const markAsRead = useCallback(async (id: number) => {
    try {
      const notification = notifications.find(n => n.id === id);
      if (!notification) return;
      
      if (notification.read) {
        // If already read, mark as unread
        await notificationApi.markAsUnread(id);
        setNotifications(prev => prev.map(n => 
          n.id === id ? { ...n, read: false } : n
        ));
        setUnreadCount(prev => prev + 1);
      } else {
        // If unread, mark as read
        await notificationApi.markAsRead(id);
        setNotifications(prev => prev.map(n => 
          n.id === id ? { ...n, read: true } : n
        ));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to toggle notification read status:', error);
    }
  }, [notifications]);

  const markAsUnread = useCallback(async (id: number) => {
    try {
      await notificationApi.markAsUnread(id);
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, read: false } : n
      ));
      setUnreadCount(prev => prev + 1);
    } catch (error) {
      console.error('Failed to mark notification as unread:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, []);

  const deleteNotification = useCallback(async (id: number) => {
    try {
      await notificationApi.deleteNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      // Decrease unread count if the deleted notification was unread
      const deletedNotification = notifications.find(n => n.id === id);
      if (deletedNotification && !deletedNotification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  }, [notifications]);

  // Load notifications on mount and when auth state changes
  useEffect(() => {
    if (isAuthenticated) {
      const cachedBootstrap = queryClient.getQueryData<DashboardBootstrapResponse>(['dashboard', 'bootstrap']);
      if (cachedBootstrap?.notifications) {
        setNotifications(cachedBootstrap.notifications?.recent ?? []);
        setUnreadCount(cachedBootstrap.notifications?.unreadCount ?? 0);
        hydratedFromDashboardRef.current = true;
        lastFetchRef.current = Date.now();
      } else {
        hydratedFromDashboardRef.current = false;
      }
      refreshNotifications(!hydratedFromDashboardRef.current);
    } else {
      // Clear notifications when user logs out
      setNotifications([]);
      setUnreadCount(0);
      hydratedFromDashboardRef.current = false;
      lastFetchRef.current = 0;
    }
  }, [isAuthenticated, refreshNotifications, queryClient]);

  // Optionally refresh every 5 minutes (only if authenticated)
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => refreshNotifications(), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated, refreshNotifications]);

  // Real-time WebSocket listeners for notifications
  useSocket({
    enabled: isAuthenticated,
    events: {
      'notification:new': (data: unknown) => {
        const notification = data as Notification;
        // Add new notification to the top of the list
        setNotifications(prev => {
          // Avoid duplicates
          if (prev.some(n => n.id === notification.id)) return prev;
          return [notification, ...prev].slice(0, 10); // Keep max 10
        });
        setUnreadCount(prev => prev + 1);
        // Show toast for the new notification
        addNotification(notification.message || notification.title, 'info');
      },
      'notification:count': (data: unknown) => {
        const { count } = data as { count: number };
        setUnreadCount(count);
      },
      'notification:read': (data: unknown) => {
        const { notificationId, read } = data as { notificationId: number; read: boolean };
        setNotifications(prev => prev.map(n =>
          n.id === notificationId ? { ...n, read } : n
        ));
      },
      'notification:read-all': () => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        setUnreadCount(0);
      },
      'notification:deleted': (data: unknown) => {
        const { notificationId } = data as { notificationId: number };
        setNotifications(prev => {
          const deleted = prev.find(n => n.id === notificationId);
          if (deleted && !deleted.read) {
            setUnreadCount(c => Math.max(0, c - 1));
          }
          return prev.filter(n => n.id !== notificationId);
        });
      },
      'notification:clear': () => {
        setNotifications([]);
        setUnreadCount(0);
      }
    }
  });

  const value = {
    // Toast notifications
    toastNotifications,
    addNotification,
    removeNotification,
    clearNotifications,
    
    // Bell notifications
    notifications,
    unreadCount,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    loading,
    refreshNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {/* Simple toast notification display */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toastNotifications.map(notification => (
          <div
            key={notification.id}
            className={`
              px-4 py-3 rounded-lg shadow-lg text-white transition-all duration-300
              ${notification.type === 'success' ? 'bg-green-500' : ''}
              ${notification.type === 'error' ? 'bg-red-500' : ''}
              ${notification.type === 'warning' ? 'bg-yellow-500' : ''}
              ${notification.type === 'info' ? 'bg-gray-500' : ''}
            `}
          >
            <div className="flex items-center justify-between">
              <span>{notification.message}</span>
              <button
                onClick={() => removeNotification(notification.id)}
                className="ml-4 text-white hover:text-gray-200"
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>
    </NotificationContext.Provider>
  );
};
